// FEAT-023: QR 위치 설정 오버레이
// - react-rnd 기반 드래그 + 리사이즈
// - 컨테이너 크기를 측정하여 비율(%) ↔ 픽셀 변환
// - 드래그 중(onDrag)은 내부 상태만 갱신 (rAF throttle), Redux dispatch 는
//   onDragStop / onResizeStop 에서만 수행 (성능: 드래그 60fps re-render 폭증 방지)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { clampQrBox, toPctFromPx } from '../../lib/voucherTemplate';

const QrPositionOverlay = ({
    qrBox,
    onChange,
    children,
    // 편집 UI는 max-width 1024px 썸네일로만 렌더 (스펙 AC-10b)
    maxWidth = 1024,
}) => {
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // 초기 + 리사이즈 감지 — container 의 실제 렌더 크기를 측정
    useEffect(() => {
        const measure = () => {
            if (!containerRef.current) return;
            const { width, height } = containerRef.current.getBoundingClientRect();
            setContainerSize({ width, height });
        };
        measure();
        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(measure);
            if (containerRef.current) ro.observe(containerRef.current);
            return () => ro.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // 현재 qrBox 의 비율을 컨테이너 크기 기준 픽셀로 환산 (Rnd 는 px 기준).
    // Rnd 내부 shallow compare 를 통과하여 불필요한 재배치를 막도록 참조 안정화.
    const pxPos = useMemo(() => ({
        x: (qrBox.xPct / 100) * containerSize.width,
        y: (qrBox.yPct / 100) * containerSize.height,
    }), [qrBox.xPct, qrBox.yPct, containerSize.width, containerSize.height]);
    const pxSize = useMemo(() => ({
        width: (qrBox.widthPct / 100) * containerSize.width,
        height: (qrBox.heightPct / 100) * containerSize.height,
    }), [qrBox.widthPct, qrBox.heightPct, containerSize.width, containerSize.height]);

    // 드래그/리사이즈 종료 시에만 onChange — react-rnd 가 내부적으로 부드러운 움직임을
    // 처리하므로 별도 rAF throttle 불필요. Redux dispatch 는 부모 책임.
    const handleDragStop = (_e, d) => {
        const pct = toPctFromPx(
            { x: d.x, y: d.y, width: pxSize.width, height: pxSize.height },
            containerSize,
        );
        onChange?.(clampQrBox({
            ...pct,
            widthPct: qrBox.widthPct,
            heightPct: qrBox.heightPct,
        }));
    };

    const handleResizeStop = (_e, _dir, ref, _delta, pos) => {
        const newPx = {
            x: pos.x,
            y: pos.y,
            width: ref.offsetWidth,
            height: ref.offsetHeight,
        };
        const pct = toPctFromPx(newPx, containerSize);
        // Phase 1: 정사각 유지 — widthPct = heightPct (짧은 쪽 기준)
        const side = Math.min(pct.widthPct, pct.heightPct);
        onChange?.(clampQrBox({
            xPct: pct.xPct,
            yPct: pct.yPct,
            widthPct: side,
            heightPct: side,
        }));
    };

    return (
        <div
            ref={containerRef}
            className="voucher-template-container"
            style={{
                position: 'relative',
                maxWidth: `${maxWidth}px`,
                width: '100%',
                margin: '0 auto',
            }}
        >
            {children}
            {containerSize.width > 0 && (
                <Rnd
                    position={pxPos}
                    size={pxSize}
                    bounds="parent"
                    lockAspectRatio={true}
                    onDragStop={handleDragStop}
                    onResizeStop={handleResizeStop}
                    style={{
                        border: '2px dashed #0d6efd',
                        background: 'rgba(13, 110, 253, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0d6efd',
                        fontWeight: 600,
                        cursor: 'move',
                        boxSizing: 'border-box',
                        // 배경 이미지 Rnd(z-index 1) 보다 위에 노출되도록 명시.
                        zIndex: 10,
                    }}
                >
                    QR
                </Rnd>
            )}
        </div>
    );
};

export default QrPositionOverlay;
